/**
 * Channel.tsx — /community/:channelSlug. One channel's thread list with an order control
 * (recent | most liked, CH-F07) and, when the viewer may post, a "Start a thread" composer
 * (CH-F02), on the house design system.
 *
 * SAME MIGRATION AS THE LANDING. This page now wears `.cm-*` (communityPage.css) — the full-bleed
 * navy band flush under the header, then sections on the shared 64px inset — the same silhouette as
 * /community, /learning and /me. The old `--tb-*` slice (COMMUNITY_STYLES, ProvisionalBanner, the
 * `.tb-list` / `.tb-inline-form` markup) is gone from this file; ThreadView.tsx still imports those
 * from ./shared, so they STAY there. `Channel` and `channelLoader` keep their names — router.tsx is
 * untouched — and every backend call is byte-for-byte what it was before the reskin.
 *
 * THE COMPOSER STAYS INLINE. Unlike the landing's Ask-for-help (an occasional aside, so an overlay),
 * starting a thread is the primary thing you do on a channel page, so it stays visible above the list
 * for anyone who may post — a surface card, on the design-system fields, not a dialog.
 *
 * SECURITY (unchanged). The loader resolves the channel from the shared CHANNELS constant only to map
 * the slug to the numeric id the API expects; the BACKEND decides whether the caller may read it. A
 * restricted channel the caller cannot read returns 404 from the threads endpoint, which we surface as
 * a plain not-found — the same response as a genuinely missing channel, so existence never leaks
 * (CH-F15/NFR03). The composer only submits; the author is derived from the session server-side.
 */
import { useState } from 'react';
import { Link, useLoaderData, useNavigate, useRevalidator } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { apiGet, apiPost, type ThreadSummary } from './api';
import { CHANNELS_BY_SLUG, SignedOut, relativeWhen } from './shared';
import { monogram, resolveAvatar } from '../avatar';
// NOTE: communityPage.css (the `.cm-*` frame) is imported at the app root (src/main.tsx), NOT here —
// see the note in Community.tsx: a component-level import code-splits into the route chunk and flashes
// the full-bleed navy band in default flow on a cold load.

/** The order the thread list is shown in. Mirrors the backend's ThreadOrder. */
type Order = 'recent' | 'likes';

export type ChannelLoaderData =
  | {
      state: 'ok';
      channelId: number;
      slug: string;
      name: string;
      canPost: boolean;
      order: Order;
      threads: ThreadSummary[];
    }
  | { state: 'signedOut' }
  | { state: 'notFound'; slug: string };

/**
 * GET /api/community/channels (for canPost + the display name) then
 * GET /api/community/channels/:id/threads?order=… . The channel must be one the viewer may read:
 * if it is absent from the channels list OR the threads call 404s, we return notFound (no leak).
 * `order` comes from the ?order= query so it survives back/forward and a shared link.
 */
export async function channelLoader({
  params,
  request,
}: LoaderFunctionArgs): Promise<ChannelLoaderData> {
  const slug = params.channelSlug ?? '';
  const known = CHANNELS_BY_SLUG.get(slug);
  const url = new URL(request.url);
  const order: Order = url.searchParams.get('order') === 'likes' ? 'likes' : 'recent';

  // An unknown slug can never be a readable channel — not found, without asking the backend.
  if (!known) return { state: 'notFound', slug };

  // Confirm the viewer may actually READ this channel (the backend is the authority), and get canPost.
  const channelsRes = await apiGet<{
    channels: { id: number; slug: string; name: string; canPost: boolean }[];
  }>('/api/community/channels');
  if (channelsRes.kind === 'unauthorized') return { state: 'signedOut' };
  if (channelsRes.kind === 'error')
    throw new Error(`GET /api/community/channels failed (${channelsRes.status})`);
  const channel = channelsRes.body.channels.find((c) => c.slug === slug);
  if (!channel) return { state: 'notFound', slug }; // readable-set excludes it → treat as not found

  const threadsRes = await apiGet<{ threads: ThreadSummary[] }>(
    `/api/community/channels/${channel.id}/threads?order=${order}`,
  );
  if (threadsRes.kind === 'unauthorized') return { state: 'signedOut' };
  if (threadsRes.kind === 'error') {
    // 404 = forbidden-or-missing, reported identically (CH-F15). Anything else is a real fault.
    if (threadsRes.status === 404) return { state: 'notFound', slug };
    throw new Error(`GET /api/community/threads failed (${threadsRes.status})`);
  }

  return {
    state: 'ok',
    channelId: channel.id,
    slug,
    name: channel.name,
    canPost: channel.canPost,
    order,
    threads: threadsRes.body.threads,
  };
}

/**
 * A one-line "what this channel is for", keyed by the shared slug — the same copy the landing shows on
 * each channel card, kept in step here so the band's lede matches the card you clicked to reach it. The
 * backend's channel summary carries no description; this is presentation copy, not an authority (the
 * server still decides which channels the caller sees), and an unknown slug falls back to a generic line.
 */
const CHANNEL_BLURBS: Readonly<Record<string, string>> = {
  questions: 'Ask a question and get help from colleagues across the business.',
  'promoted-tools': 'Discover tools worth trying, highlighted for the whole community.',
  'swat-suggestions': 'Suggest improvements and raise ideas for the SWAT team.',
  'champion-announcements': 'Read the latest updates and announcements from Champions.',
  'champions-only': 'A private space for Champions to share notes and coordinate.',
  'swat-only': 'A private space for the SWAT team to plan and compare notes.',
};
function channelBlurb(slug: string): string {
  return CHANNEL_BLURBS[slug] ?? 'Read and join the conversation with your colleagues.';
}

/*
 * ─── Derived, presentation-only signals ──────────────────────────────────────
 * Everything below is computed CLIENT-SIDE from the ThreadSummary fields the list
 * already carries — no new backend, no new fetch. They only summarise and RANK the
 * threads for display (the band's figures, the activity rail's ordering); the backend
 * remains the authority on what the caller may see (the loader already gate-kept that),
 * and none of this claims a server-side status.
 */
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/** Total engagement of a thread — the signal the activity rail's "Most active" ranks on. */
function weight(t: ThreadSummary): number {
  return t.replyCount + t.likeCount;
}

/*
 * `isActiveThisWeek` reads the clock ITSELF (like relativeWhen in shared.tsx), rather than taking
 * `now` as a param computed in a component body — calling Date.now() directly in render trips
 * react-hooks/purity, but inside a plain helper it is fine (see FlowPickerReskin).
 */
/** Active this week = last post within seven days. Drives the band's "active" figure. */
function isActiveThisWeek(t: ThreadSummary): boolean {
  const last = new Date(t.lastPostAt).getTime();
  return !Number.isNaN(last) && Date.now() - last < WEEK_MS;
}

/** The band's at-a-glance figures, all summed from the visible list. */
function StatsStrip({ threads }: { threads: ThreadSummary[] }) {
  const totalReplies = threads.reduce((n, t) => n + t.replyCount, 0);
  const activeThisWeek = threads.filter((t) => isActiveThisWeek(t)).length;
  const stats: { num: number; label: string }[] = [
    { num: threads.length, label: threads.length === 1 ? 'thread' : 'threads' },
    { num: totalReplies, label: totalReplies === 1 ? 'reply' : 'replies' },
    { num: activeThisWeek, label: 'active this week' },
  ];
  return (
    <ul className="cm-band__stats">
      {stats.map((s) => (
        <li className="cm-stat" key={s.label}>
          <span className="cm-stat__num">{s.num}</span>
          <span className="cm-stat__label">{s.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** A round monogram chip standing in for the author, initials only (the activity rail's people
 *  strip has names but no avatar refs, so it uses this). */
function Chip({ name, className }: { name: string | null; className?: string }) {
  return (
    <span className={`cm-chip${className ? ` ${className}` : ''}`} aria-hidden="true">
      {monogram(name ?? '?')}
    </span>
  );
}

/**
 * The starter's avatar for a thread row: their real profile photo when there is one, else the
 * monogram. The stored ref is resolved through the SAME allowlist chokepoint the profile and
 * thread pages use (avatar.ts resolveAvatar) — a stock pick maps to a bundled asset, a validated
 * upload data: URI is emitted as-is, anything else falls through to the directory photo, else null
 * → the monogram. A hostile ref can therefore never smuggle a URL into <img src>. A departed
 * author (name === null) has had every avatar field nulled upstream (CH-F04), so this shows the
 * neutral monogram, aria-hidden so the byline text carries the identity for screen readers. The
 * image fills the same round `.cm-chip` shell the monogram uses, so the row layout is unchanged. */
function AvatarChip({ thread }: { thread: ThreadSummary }) {
  const src = resolveAvatar(
    { avatarKind: thread.avatarKind, avatarBlobRef: thread.avatarBlobRef },
    thread.photoBlobRef,
  );
  if (src) {
    return (
      // eslint-disable-next-line jsx-a11y/img-redundant-alt -- alt is a name, intentional
      <img
        className="cm-chip cm-chip--photo"
        src={src}
        alt={thread.authorName ? `${thread.authorName}'s profile photo` : ''}
      />
    );
  }
  return <Chip name={thread.authorName} />;
}

/**
 * One rich thread row: author chip · (title + author/time meta) · a right-pulled
 * replies/likes stat cluster. The whole card is a single link to the thread, exactly
 * as the simple row was. Which threads are busy is read straight from the stat cluster;
 * there is no derived "hot" badge (nothing on the backend backs one).
 */
function ThreadRow({ thread }: { thread: ThreadSummary }) {
  return (
    <li className="cm-thread cm-thread--rich">
      <Link className="cm-thread__link" to={`/community/thread/${thread.threadId}`}>
        <AvatarChip thread={thread} />
        <span className="cm-thread__body">
          <span className="cm-thread__title">{thread.title}</span>
          <span className="cm-thread__meta">
            <span className="cm-thread__by">By {thread.authorName ?? '[former colleague]'}</span>
            <span className="cm-thread__stat">Last activity {relativeWhen(thread.lastPostAt)}</span>
          </span>
        </span>
        <span className="cm-thread__stats">
          <span className="cm-metric">
            <span className="cm-metric__num">{thread.replyCount}</span>
            <span className="cm-metric__label">
              {thread.replyCount === 1 ? 'reply' : 'replies'}
            </span>
          </span>
          <span className="cm-metric">
            <span className="cm-metric__num">{thread.likeCount}</span>
            <span className="cm-metric__label">{thread.likeCount === 1 ? 'like' : 'likes'}</span>
          </span>
        </span>
      </Link>
    </li>
  );
}

/**
 * The activity rail (aside): "Most active" (top threads by engagement) and "Recently
 * active" (the people leading the newest threads). Both derived from the same list —
 * a fuller page, no extra request. Rendered only when there is something to show.
 */
function ActivityRail({ threads, slug }: { threads: ThreadSummary[]; slug: string }) {
  const topActive = [...threads]
    .filter((t) => weight(t) > 0)
    .sort((a, b) => weight(b) - weight(a))
    .slice(0, 5);

  // The people whose names lead the most-recently-active threads, de-duplicated,
  // newest first. Names only — ThreadSummary carries no id, so these are chips, not links.
  const recentPeople: string[] = [];
  for (const t of [...threads].sort(
    (a, b) => new Date(b.lastPostAt).getTime() - new Date(a.lastPostAt).getTime(),
  )) {
    const name = t.authorName;
    if (name && !recentPeople.includes(name)) recentPeople.push(name);
  }
  const shownPeople = recentPeople.slice(0, 6);
  const morePeople = recentPeople.length - shownPeople.length;

  if (topActive.length === 0 && shownPeople.length === 0) return null;

  return (
    <aside className="cm-rail" aria-label="Channel activity">
      {topActive.length > 0 ? (
        <section className="cm-rail__card" aria-labelledby={`rail-active-${slug}`}>
          <p className="cm-rail__title" id={`rail-active-${slug}`}>
            Most active
          </p>
          <ol className="cm-rail__list">
            {topActive.map((t) => (
              <li className="cm-rail__item cm-rail__item--ranked" key={t.threadId}>
                <Link className="cm-rail__link" to={`/community/thread/${t.threadId}`}>
                  {t.title}
                </Link>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {shownPeople.length > 0 ? (
        <section className="cm-rail__card" aria-labelledby={`rail-people-${slug}`}>
          <p className="cm-rail__title" id={`rail-people-${slug}`}>
            Recently active
          </p>
          <div className="cm-rail__people">
            {shownPeople.map((name) => (
              <Chip key={name} name={name} className="cm-chip--sm" />
            ))}
            {morePeople > 0 ? (
              <span className="cm-chip cm-chip--sm cm-chip--more" aria-hidden="true">
                +{morePeople}
              </span>
            ) : null}
          </div>
        </section>
      ) : null}
    </aside>
  );
}

/** The order control — two links that rewrite ?order=, so the loader re-runs (no client state). */
function OrderControl({ slug, order }: { slug: string; order: Order }) {
  return (
    <p className="cm-order">
      <Link
        to={`/community/${slug}?order=recent`}
        aria-current={order === 'recent' ? 'true' : undefined}
      >
        Most recent
      </Link>
      <Link
        to={`/community/${slug}?order=likes`}
        aria-current={order === 'likes' ? 'true' : undefined}
      >
        Most liked
      </Link>
    </p>
  );
}

/**
 * The "Start a thread" composer. On submit it POSTs the new thread, then navigates straight to the
 * created thread. Local state only for the field values, the busy flag and an error line — no global
 * store. Rendered only when the backend says the viewer may post in this channel.
 */
function Composer({ channelId }: { channelId: number }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const res = await apiPost<{ thread_id: string }>('/api/community/threads', {
      channelId,
      title: title.trim(),
      body: body.trim(),
    });
    setBusy(false);
    if (res.kind === 'ok') {
      navigate(`/community/thread/${res.body.thread_id}`);
      return;
    }
    if (res.kind === 'unauthorized') {
      setError('Your session has expired. Please sign in again.');
      return;
    }
    if (res.kind === 'error' && res.code === 'invalid_request') {
      setError('Please give a title (at least 3 characters) and a message.');
      return;
    }
    if (res.kind === 'error' && res.status === 403) {
      setError('You cannot post in this channel.');
      return;
    }
    setError('Something went wrong starting the thread. Please try again.');
  }

  const canSubmit = title.trim().length >= 3 && body.trim().length >= 1 && !busy;

  return (
    <form className="cm-composer" onSubmit={submit}>
      {error ? (
        <p className="cm-error" role="alert">
          {error}
        </p>
      ) : null}
      <fieldset className="cm-composer__fields">
        <label className="cm-field">
          <span>Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="What is your question or topic?"
          />
        </label>
        <label className="cm-field">
          <span>Message</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={10_000}
            placeholder="Add the detail your colleagues need to help."
          />
        </label>
      </fieldset>
      <div className="cm-composer__foot">
        <button className="cm-btn-solid" type="submit" disabled={!canSubmit}>
          {busy ? 'Posting…' : 'Post thread'}
        </button>
      </div>
    </form>
  );
}

export function Channel() {
  const data = useLoaderData() as ChannelLoaderData;
  const revalidator = useRevalidator();

  if (data.state === 'signedOut') return <SignedOut title="Community" returnTo="/community" />;

  if (data.state === 'notFound') {
    return (
      <section aria-labelledby="channel-missing-heading" className="tb-community cm-page">
        <div className="cm-band-bleed">
          <div className="cm-band">
            <div className="cm-band__main">
              <p className="cm-crumb">
                <Link to="/community">← Community</Link>
              </p>
              <h1 id="channel-missing-heading">Channel not found</h1>
              <p className="cm-band__lede">
                This channel does not exist, or is not available to you.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const { channelId, slug, name, canPost, order, threads } = data;

  return (
    <section aria-labelledby="channel-heading" className="tb-community cm-page">
      {/* ── Band: breadcrumb back to the hub, the channel name, the one-line "what you do here"
             (the same blurb its landing card carries), and an at-a-glance stats strip. ── */}
      <div className="cm-band-bleed">
        <div className="cm-band">
          <div className="cm-band__main">
            <p className="cm-crumb">
              <Link to="/community">← Community</Link>
            </p>
            <h1 id="channel-heading">{name}</h1>
            <p className="cm-band__lede">{channelBlurb(slug)}</p>
            {threads.length > 0 ? <StatsStrip threads={threads} /> : null}
          </div>
        </div>
      </div>

      {/* ── Start a thread (CH-F02): inline, above the list, for anyone who may post. ── */}
      {canPost ? (
        <section aria-labelledby="compose-heading" className="cm-section">
          <div className="cm-section-head">
            <h2 id="compose-heading">Start a thread</h2>
          </div>
          <Composer channelId={channelId} />
        </section>
      ) : null}

      {/* ── Threads: rich rows in the main column, an activity rail alongside, with the
             recent | most-liked order control pinned right of the section head. ── */}
      <section aria-labelledby="threads-heading" className="cm-section">
        <div className="cm-section-head">
          <h2 id="threads-heading">Threads</h2>
          <OrderControl slug={slug} order={order} />
        </div>

        {threads.length === 0 ? (
          <p className="cm-empty">
            No threads here yet{canPost ? ' — start the first one above.' : '.'}
          </p>
        ) : (
          <div className="cm-channel-body">
            <ul className="cm-threads">
              {threads.map((t) => (
                <ThreadRow key={t.threadId} thread={t} />
              ))}
            </ul>
            <ActivityRail threads={threads} slug={slug} />
          </div>
        )}

        {/* A quiet manual refresh; the composer navigates away on success, so this is for likes/replies
            made in another tab. Uses the router's revalidator rather than a full reload. */}
        <p style={{ marginTop: '16px' }}>
          <button
            className="cm-btn-ghost cm-btn-ghost--refresh"
            type="button"
            onClick={() => revalidator.revalidate()}
            disabled={revalidator.state === 'loading'}
          >
            {revalidator.state === 'loading' ? 'Refreshing…' : 'Refresh'}
          </button>
        </p>
      </section>
    </section>
  );
}
